import { createFileRoute, useNavigate, redirect, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { devAutoLoginEnabled, devLoginAccounts, devSignIn } from "@/lib/dev/test-login";
import { Trans, useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { isRecoveryCallback } from "@/lib/auth/recovery-link";
import { lovable } from "@/integrations/lovable";
import { mapAuthError, isEmailNotConfirmedError } from "@/lib/auth/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Panel } from "@/components/game/Panel";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LegalFooter } from "@/components/LegalFooter";
import { toast } from "sonner";

/** Mikro-Label über den Eingabefeldern: Lilita One, Versalien, weite Laufweite. */
const FIELD_LABEL =
  "mb-1.5 block font-display text-[10px] uppercase tracking-[0.12em] text-muted-foreground";
/** Waagerechte Ink-Linie des „oder"-Trenners. */
const DIVIDER = "my-4 flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground";
/** Eingefasster Hinweis-/Unterbereich innerhalb der Karte (Art-Blau = Information). */
const INSET = "rounded-md border-2 border-accent bg-ink/40 p-4";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Login – Xenomons" },
      { name: "description", content: "Melde dich an und erwecke dein Xenomon." },
      { property: "og:title", content: "Login – Xenomons" },
      { property: "og:description", content: "Melde dich an und erwecke dein Xenomon." },
      { property: "og:url", content: "https://xenomons.online/auth" },
    ],
    links: [
      { rel: "canonical", href: "https://xenomons.online/auth" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    // A password-recovery link signs the visitor in. Bouncing them to /game
    // here would swallow the reset: they end up playing with the old password
    // and never see the form. Stand down and let the root route send them to
    // /reset-password once Supabase has read the fragment.
    if (isRecoveryCallback(window.location.hash, window.location.search)) return;
    const { data } = await supabase.auth.getUser();
    if (data.user) throw redirect({ to: "/game" });
  },
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [signinError, setSigninError] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signinEmailNotConfirmed, setSigninEmailNotConfirmed] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmSent, setConfirmSent] = useState<{ email: string } | null>(null);
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  // Einmal beim Aufbau gelesen: `import.meta.env` ändert sich zur Laufzeit
  // nicht, und so steht im Produktions-Build eine leere Liste da, die Rollup
  // mitsamt dem Zweig entfernen kann.
  const [devAccounts] = useState(devLoginAccounts);
  const [devBusy, setDevBusy] = useState<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // A recovery link also arrives with a session. Sending it to /game would
      // swallow the password reset; the root route routes it to the form.
      if (event === "PASSWORD_RECOVERY") return;
      if (session) {
        if (event === "SIGNED_IN") {
          try { sessionStorage.setItem("xeno_beta_notice_pending", "1"); } catch { /* ignore */ }
        }
        navigate({ to: "/game", replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Nur lokal und nur mit VITE_DEV_AUTOLOGIN=1: einmalig anmelden, damit man
  // beim Testen nicht nach jedem Sitzungswechsel wieder tippt. Die
  // Weiterleitung übernimmt der `onAuthStateChange`-Effekt oben.
  useEffect(() => {
    const stefan = devAccounts.find((a) => a.id === "stefan");
    if (!stefan || !devAutoLoginEnabled()) return;
    let abgebrochen = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (abgebrochen || data.session) return;
      const r = await devSignIn("stefan");
      if (!abgebrochen && !r.ok) toast.error(r.error ?? "Test-Anmeldung fehlgeschlagen.");
    })();
    return () => {
      abgebrochen = true;
    };
  }, [devAccounts]);

  // Surface OAuth errors that come back as ?error / ?error_description (or in the hash).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const qs = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const errDesc = qs.get("error_description") ?? hash.get("error_description") ?? "";
    const err = qs.get("error") ?? hash.get("error") ?? "";
    if (!err && !errDesc) return;
    if (errDesc) {
      toast.error(errDesc);
    }
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    window.history.replaceState({}, "", url.toString());
  }, [t]);

  function stashConsent() {
    try {
      sessionStorage.setItem("xeno_marketing_pending", marketing ? "1" : "0");
    } catch {
      /* ignore */
    }
  }

  function stashFeedbackPrompt() {
    try {
      sessionStorage.setItem("xeno_feedback_prompt_pending", "1");
      localStorage.setItem("xenomon.sound.on", "0");
      localStorage.setItem("xenomon.haptic.on", "0");
    } catch {
      /* ignore */
    }
  }


  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setSigninError(null);
    setSigninEmailNotConfirmed(false);
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    setLoading(false);
    if (error) {
      setSigninError(mapAuthError(error.message, t));
      if (isEmailNotConfirmedError(error.message)) setSigninEmailNotConfirmed(true);
      return;
    }
    if (data.session) {
      try { sessionStorage.setItem("xeno_beta_notice_pending", "1"); } catch { /* ignore */ }
      navigate({ to: "/game", replace: true });
    }
  }

  async function resendConfirmation() {
    const target = email.trim().toLowerCase();
    if (!target) {
      toast.error(t("auth:gate.errors.emailRequired"));
      return;
    }
    setResending(true);
    // Without an explicit target Supabase falls back to the project's Site URL,
    // which points at the Lovable preview — a player who resends the
    // confirmation would land on the preview instead of the site they signed up
    // on. Same target the signup itself passes.
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: target,
      options: { emailRedirectTo: window.location.origin + "/auth" },
    });
    setResending(false);
    if (error) {
      toast.error(mapAuthError(error.message, t) || t("auth:toasts.confirmationResendFailed"));
      return;
    }
    toast.success(t("auth:toasts.confirmationResent"));
  }

  async function googleSignIn() {
    setLoading(true);
    const res = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/auth",
      extraParams: { prompt: "select_account" },
    });
    if (res.error) {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate({ to: "/game", replace: true });
        return;
      }
      toast.error(t("auth:toasts.googleFailed"));
      setLoading(false);
    }
  }

  async function signUpEmail(e: React.FormEvent) {
    e.preventDefault();
    setSignupError(null);
    setLoading(true);
    const normalized = email.trim().toLowerCase();
    stashConsent();
    stashFeedbackPrompt();

    const { error } = await supabase.auth.signUp({
      email: normalized,
      password,
      options: { emailRedirectTo: window.location.origin + "/auth" },
    });
    setLoading(false);
    if (error) {
      setSignupError(mapAuthError(error.message, t));
      return;
    }
    setConfirmSent({ email: normalized });
  }

  async function signUpGoogle() {
    setLoading(true);
    stashConsent();
    stashFeedbackPrompt();

    const res = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/auth",
      extraParams: { prompt: "select_account" },
    });
    if (res.error) {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate({ to: "/game", replace: true });
        return;
      }
      toast.error(t("auth:toasts.googleFailed"));
      setLoading(false);
    }
  }

  async function sendResetEmail(e: React.FormEvent) {
    e.preventDefault();
    const target = (resetEmail || email).trim().toLowerCase();
    if (!target) {
      toast.error(t("auth:gate.errors.emailRequired"));
      return;
    }
    setResetLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: window.location.origin + "/reset-password",
    });
    setResetLoading(false);
    if (error) {
      toast.error(mapAuthError(error.message, t));
      return;
    }
    setResetSent(true);
    toast.success(t("auth:forgot.sentTitle"));
  }

  return (
    // `justify-center` in einem `min-h-screen`-Container schneidet oben ab,
    // sobald der Inhalt hoeher wird als das Fenster — und der abgeschnittene
    // Teil ist NICHT scrollbar. Stattdessen `my-auto` auf dem Panel: Es
    // zentriert bei Platz und laesst oben scrollen, wenn es eng wird.
    // Sichtbar wurde das lokal durch die Testkonten-Kachel; auf kleinen
    // Geraeten trifft es die Seite auch ohne sie.
    <div className="relative flex min-h-screen flex-col items-center px-4 py-10">
      <div className="halftone pointer-events-none absolute inset-0 opacity-70" aria-hidden />
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher variant="outline" showLabel />
      </div>

      <Panel grid className="relative z-10 my-auto w-full max-w-md" contentClassName="p-5 sm:p-7">
        <div className="mb-5 text-center">
          <h1 className="text-3xl">
            XENOMONS<span className="text-primary">.</span>
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("common:app.tagline")}</p>
        </div>

        {devAccounts.length > 0 ? (
          <div className="mb-4 rounded-md border-2 border-dashed border-accent bg-ink/40 p-3">
            <p className="font-display text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              Nur lokal · Testkonten
            </p>
            <div className="mt-2 space-y-2">
              {devAccounts.map((konto) => (
                <Button
                  key={konto.id}
                  variant="outline"
                  className="w-full justify-start"
                  disabled={devBusy !== null}
                  onClick={async () => {
                    setDevBusy(konto.id);
                    const r = await devSignIn(konto.id);
                    setDevBusy(null);
                    if (!r.ok) toast.error(r.error ?? "Test-Anmeldung fehlgeschlagen.");
                  }}
                >
                  {devBusy === konto.id ? (
                    "Melde an…"
                  ) : (
                    <>
                      <span className="font-extrabold">{konto.label}</span>
                      <span className="ml-2 truncate text-xs font-semibold text-muted-foreground">
                        {konto.email}
                      </span>
                    </>
                  )}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "signin" | "signup")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin">{t("auth:tabs.signin")}</TabsTrigger>
            <TabsTrigger value="signup">{t("auth:tabs.signup")}</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <Button onClick={googleSignIn} variant="secondary" className="mt-4 w-full">
              {t("auth:buttons.googleSignin")}
            </Button>
            <div className={DIVIDER}>
              <div className="h-0.5 flex-1 bg-ink" /> {t("auth:divider")} <div className="h-0.5 flex-1 bg-ink" />
            </div>
            <form onSubmit={signIn} className="space-y-3">
              <div>
                <Label htmlFor="email" className={FIELD_LABEL}>{t("auth:fields.email")}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="pw" className={FIELD_LABEL}>{t("auth:fields.password")}</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {signinError && (
                <div className="rounded-md border-2 border-destructive bg-destructive/15 p-3 text-xs leading-relaxed text-destructive-foreground">
                  <p>{signinError}</p>
                  {signinEmailNotConfirmed && (
                    <button
                      type="button"
                      onClick={resendConfirmation}
                      disabled={resending}
                      className="mt-2 min-h-11 font-extrabold underline underline-offset-2 hover:text-foreground disabled:opacity-45"
                    >
                      {resending ? t("auth:buttons.loading") : t("auth:resendConfirmation")}
                    </button>
                  )}
                </div>
              )}
              {/* Der eine orange Knopf dieser Ansicht. */}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t("auth:buttons.loading") : t("auth:buttons.signin")}
              </Button>
            </form>
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => {
                  setForgotOpen((v) => !v);
                  setResetSent(false);
                  if (!resetEmail) setResetEmail(email);
                }}
                className="min-h-11 text-xs font-extrabold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                {t("auth:forgot.link", "Passwort vergessen?")}
              </button>
            </div>
            {forgotOpen && (
              <div className={`mt-1 ${INSET}`}>
                {resetSent ? (
                  <div className="space-y-2 text-center">
                    <p className="font-display text-sm text-foreground">{t("auth:forgot.sentTitle")}</p>
                    <p className="text-xs leading-relaxed text-muted-foreground">{t("auth:forgot.sentDescription")}</p>
                    <button
                      type="button"
                      onClick={() => { setForgotOpen(false); setResetSent(false); }}
                      className="min-h-11 text-xs font-extrabold text-muted-foreground underline underline-offset-2 hover:text-foreground"
                    >
                      {t("auth:forgot.close")}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={sendResetEmail} className="space-y-3">
                    <div>
                      <p className="font-display text-[11px] uppercase tracking-[0.12em] text-foreground">
                        {t("auth:forgot.title")}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("auth:forgot.description")}</p>
                    </div>
                    <div>
                      <Label htmlFor="reset-email" className={FIELD_LABEL}>{t("auth:forgot.emailLabel")}</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="submit" variant="secondary" disabled={resetLoading} className="min-w-[8rem] flex-1">
                        {resetLoading ? t("auth:buttons.loading") : t("auth:forgot.submit")}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setForgotOpen(false)}>
                        {t("auth:forgot.cancel")}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="signup">
            {confirmSent ? (
              <div className={`mt-4 text-center ${INSET}`}>
                <p className="font-display text-sm text-foreground">{t("auth:confirmEmail.title")}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                  <Trans
                    i18nKey="auth:confirmEmail.description"
                    values={{ email: confirmSent.email }}
                    components={{ strong: <strong className="text-foreground" /> }}
                  />
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t("auth:confirmEmail.hint")}</p>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 w-full"
                  onClick={() => {
                    setConfirmSent(null);
                    setActiveTab("signin");
                  }}
                >
                  {t("auth:confirmEmail.close")}
                </Button>
              </div>
            ) : (
              <>
                <p className="mt-4 rounded-md border-2 border-ink bg-secondary p-2.5 text-center text-xs leading-relaxed text-muted-foreground">
                  {t("auth:welcomeHint")}
                </p>

                <label className="mt-3 flex min-h-11 cursor-pointer items-start gap-3 rounded-md border-2 border-ink bg-secondary p-3 transition hover:brightness-110">
                  <Checkbox
                    checked={marketing}
                    onCheckedChange={(v) => setMarketing(v === true)}
                    className="mt-0.5"
                  />
                  <span className="flex flex-col text-xs leading-snug">
                    <span className="font-extrabold text-foreground">{t("auth:marketing.label")}</span>
                    <span className="mt-0.5 text-muted-foreground">{t("auth:marketing.description")}</span>
                  </span>
                </label>

                <Button onClick={signUpGoogle} disabled={loading} variant="secondary" className="mt-3 w-full">
                  {t("auth:buttons.googleSignup")}
                </Button>

                <div className={DIVIDER}>
                  <div className="h-0.5 flex-1 bg-ink" /> {t("auth:divider")} <div className="h-0.5 flex-1 bg-ink" />
                </div>
                <form onSubmit={signUpEmail} className="space-y-3">
                  <div>
                    <Label htmlFor="email2" className={FIELD_LABEL}>{t("auth:fields.email")}</Label>
                    <Input id="email2" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="pw2" className={FIELD_LABEL}>{t("auth:fields.password")}</Label>
                    <Input id="pw2" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{t("auth:errors.passwordRequirements")}</p>
                  </div>
                  {signupError && (
                    <div className="rounded-md border-2 border-destructive bg-destructive/15 p-3 text-xs leading-relaxed text-destructive-foreground">
                      {signupError}
                    </div>
                  )}
                  {/* Der eine orange Knopf dieser Ansicht. */}
                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? t("auth:buttons.loading") : t("auth:buttons.signup")}
                  </Button>
                </form>
              </>
            )}
          </TabsContent>
        </Tabs>

        <Button asChild variant="ghost" className="mt-5 w-full">
          <Link to="/">{t("auth:backToHome", "Zurück zur Startseite")}</Link>
        </Button>
      </Panel>

      <LegalFooter className="relative z-10 mt-8" />
    </div>
  );
}
